import type {
  APITaskArtifact,
  ArtifactState,
  BranchArtifact,
  BranchArtifactData,
  GitHubArtifactData,
  GitHubResourceArtifact,
} from '../types/artifact'
import type {HydratedAPITask} from '../types/api-task'
import type {Pull} from '../types/pull'

/**
 * Type guard to check if an artifact is a branch artifact.
 */
function isBranchArtifact(artifact: APITaskArtifact): artifact is BranchArtifact {
  return artifact.type === 'branch' && artifact.provider === 'github'
}

/**
 * Type guard to check if an artifact is a GitHub resource artifact.
 */
function isGitHubResourceArtifact(artifact: APITaskArtifact): artifact is GitHubResourceArtifact {
  return artifact.type === 'github_resource' && artifact.provider === 'github'
}

export const getBranchArtifactData = (artifacts?: APITaskArtifact[]): BranchArtifactData | undefined => {
  const branchArtifact = artifacts?.find(isBranchArtifact)
  return branchArtifact?.data
}

/**
 * Resolves the branch name to use for "Continue in…" affordances, preferring an
 * explicit branch name, then the task's branch artifact, then the pull request's
 * head ref. Returns '' when none are available.
 */
export function resolveEffectiveBranchName(
  branchName: string | undefined,
  task: Pick<HydratedAPITask, 'artifacts'> | undefined,
  pull: Pick<Pull, 'head_ref_name'> | undefined,
): string {
  return branchName || getBranchArtifactData(task?.artifacts)?.head_ref || pull?.head_ref_name || ''
}

/**
 * Checks if the base_ref and head_ref are the same.
 */
export function baseRefMatchesHeadRef(data?: BranchArtifactData): boolean {
  return data?.base_ref !== undefined && data.base_ref === data.head_ref
}

export const getGitHubArtifactData = (artifacts?: APITaskArtifact[]): GitHubArtifactData | undefined => {
  const ghArtifact = artifacts?.find(isGitHubResourceArtifact)
  return ghArtifact?.data
}

export function getEffectivePrState(
  artifactState: ArtifactState | undefined,
  pull: Pull | undefined,
): ArtifactState | undefined {
  // Prefer the hydrated pull object: its `reviewable_state` is the authoritative
  // draft-vs-ready signal. The artifact snapshot's collapsed `state` enum is
  // unreliable for draft (it can report `'open'` for a PR that is actually a
  // draft), so it is only used as a fallback when no pull has been hydrated.
  if (pull) {
    return pull.state === 'open' && pull.reviewable_state === 'draft' ? 'draft' : pull.state
  }
  return artifactState
}
